import "../i18next";
import "@viedoc/viedoc-ui";
import "@viedoc/viedoc-ui/css/viedoc.css";
import "../assets/styles/Custom-styles.scss";

import { Route, Routes, useLocation } from "react-router";
import { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import i18next from "i18next";
import _ from "underscore";
import Header from "./core/containers/Header/Header";
import MainContainer from "./core/containers/MainContainer/MainContainer";
import SidePanel from "./core/containers/SidePanel/SidePanel";
import * as zoneActions from "./store/actions/zoneActions";
import * as templateActions from "./store/actions/templateActions";
import * as documentActions from "./store/actions/documentActions";
import * as authActions from "./store/actions/authActions";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { storageFactory } from "../utils/storageFactory";
import { StorageHelper } from "../utils/storageHelper";
import OverviewDashboard from "./core/components/public/OverviewDashboard/OverviewDashboard";
import ZonesSections from "./core/components/public/ZonesSections/ZonesSections";
import DocumentsSection from "./core/components/public/Documents/Documents";
import TrailMasterDropZone from "./core/components/public/TrialMasterDropZone/TrialMasterDropZone";
import TmfStructure from "./core/components/public/TmfAdmin/TmfStructure";
import OtherSettings from "./core/components/public/TmfAdmin/OtherSettings";
import Templates from "./core/components/public/TmfAdmin/Templates";
import Status from "./core/components/public/Status/Status";
import Ems from "./core/containers/ArchiveDashboard/Ems";
import ArchiveDashboard from "./core/containers/ArchiveDashboard/ArchiveDashboard";
import PrivateRoutes from "./secure/routes/PrivateRoutes";
import {
  FlaggedByQcDocument,
  AwatingReviewDocument,
  FinalizedDocument,
} from "./core/components/public/Documents/DocumentFilterPages";
import * as UtilsHelper from "../utils/utilsHelper";
import useResizer from "../hooks/useResizer";
import useDetectClickOutside from "../hooks/useDetectClickOutside";
import APP_CONST from "./core/shared/constants";

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { windowDimensions } = useResizer();

  const appSettings = StorageHelper.getAppSettings();
  const sidePanelRef = useRef();

  useDetectClickOutside(sidePanelRef, (event) => {
    if (
      windowDimensions.width < APP_CONST.WINDOW_BREAKPOINT &&
      event?.target?.getAttribute("class") !== "ham-icon" &&
      event?.target?.getAttribute("name") !== "hamburger"
    ) {
      setShowSidebar(false);
      StorageHelper.setSidepanelToggle(false);
    }
  });

  const { studyUserInfo } = useSelector((store) => store.zone);
  const { userRoleList, isUserRoleListLoaded } = useSelector(
    (store) => store.auth
  );
  const { listAudit, isRoleListLoaded, orgGuid } = useSelector(
    (store) => store.zone
  );
  const {
    settings,
    isDropZoneSettingsLoaded,
    isDropZoneFileCountsLoaded,
    sharedfilecount: dropZoneSharedFileCount,
    privatefilecount: dropZonePrivateFileCount,
  } = useSelector((store) => store.template);
  const {
    isshareddropzoneenabled: isSharedDropZoneEnabled,
    isprivatedropzoneenabled: isPrivateDropZoneEnabled,
  } = settings;

  const localStore = storageFactory(() => sessionStorage);
  let tokenExpiryDate, remainingTime;
  const DURATION = 120000;
  const [showSidebar, setShowSidebar] = useState(
    StorageHelper.getSidepanelToggle()
      ? StorageHelper.getSidepanelToggle() === "true"
        ? true
        : false
      : true
  );

  if (location.pathname === "/oq" && showSidebar) {
    setShowSidebar(false);
  }

  useEffect(() => {
    setInterval(callRefreshCheck, DURATION);
    window.addEventListener("message", getEtmfToken, false);

    //consuming cookie data in the event of etmf version switch
    tokenInfoHandler();

    return () => {
      clearInterval(callRefreshCheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!_.isEmpty(studyUserInfo && studyUserInfo.language)) {
      const lang =
        (studyUserInfo.language === "en" ? "en-us" : studyUserInfo.language) ||
        "en-us";
      i18next.changeLanguage(lang);
    }
  }, [studyUserInfo]);

  function triggerInitialApiCalls() {
    dispatch(authActions.fetchDBCheck(navigate));
    dispatch(authActions.fetchRoleListView());
    dispatch(zoneActions.fetchAllClientApis());
    dispatch(templateActions.getLockStatus());
    dispatch(templateActions.getTimeFormat());
    dispatch(templateActions.getMilestoneCount());
    dispatch(templateActions.getEnablingMilestoneStatus());
    dispatch(templateActions.getTMFStructureData("onload"));
    dispatch(documentActions.fetchAndLoadDocMaster());
    if (!_.isEmpty(orgGuid)) {
      dispatch(templateActions.getAllTemplates(orgGuid));
    }
    dispatch(templateActions.getStudyInformation());
    dispatch(templateActions.getMilestoneDropDownData());
    dispatch(templateActions.getMilestoneGroup());
    dispatch(zoneActions.fetchMetricsData());
    dispatch(templateActions.getDropZoneSettings());

    if (_.isEmpty(StorageHelper.getErrorDocument())) {
      StorageHelper.setErrorDocument([]);
    }
  }

  const tokenInfoHandler = () => {
    const token = localStore.getItem("jwtToken");
    if (_.isEmpty(token)) {
      const { tokenInfo, tokenExpiry } =
        UtilsHelper.getTokenInfoCookieDetails();
      const token_info = {
        tokenInfo: tokenInfo,
        tokenexpirydate: tokenExpiry,
        usertype: "user",
      };
      localStore.setItem("jwtToken", tokenInfo);
      localStore.setItem("token_info", JSON.stringify(token_info));
    }
    const tokenInformationJSON = JSON.parse(
      sessionStorage.getItem("token_info")
    );
    tokenExpiryDate = new Date(tokenInformationJSON.tokenexpirydate);
    const currentDate = new Date();
    remainingTime = (tokenExpiryDate - currentDate) / 1000 / 60;
    localStore.setItem("mintoexpiry", remainingTime);
    if (remainingTime < 5) {
      remainingTime = 1;
    }
    triggerInitialApiCalls();
  };

  const getEtmfToken = (event) => {
    let baseUrl = window.location.origin;
    let postRequestData;
    try {
      postRequestData = JSON.parse(event.data);
    } catch (ex) {
      // To prevent throwing parsing errors incase browser plug-ins trigger post message to iframe.
    }
    // Validate post message
    if (postRequestData && postRequestData.tokenInfo) {
      let token = postRequestData.tokenInfo;
      let url = baseUrl + (token ? "/" : "/error");
      localStore.clear();
      localStore.setItem("token_info", JSON.stringify(postRequestData));
      localStore.setItem("jwtToken", postRequestData.tokenInfo);
      // To ignore messages from other browser extensions, sending typed data
      let messageData = {
        redirectUrl: url,
      };
      window.parent.postMessage(JSON.stringify(messageData), "*");
    } else {
      const isAuthed = JSON.parse(localStore.getItem("token_info"));
      const appSettings = JSON.parse(localStore.getItem("appsettings"));
      if (_.isEmpty(isAuthed) && !_.isEmpty(appSettings)) {
        window.parent.location.href = appSettings.viedocParentUrl;
      }
    }
  };

  const callRefreshCheck = () => {
    const tokenInfo = localStore.getItem("token_info");
    const tokenInfoJSON = JSON.parse(tokenInfo);
    tokenExpiryDate = new Date(tokenInfoJSON.tokenexpirydate);
    const currentTime = new Date();
    remainingTime = (tokenExpiryDate - currentTime) / 1000 / 60;
    localStore.setItem("mintoexpiry", remainingTime);
    if (remainingTime < 5) {
      dispatch(templateActions.getRefreshToken());
    }

    document.cookie = "tokenInfo=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=" + appSettings.domain + "; path=/";
    document.cookie = "tokenExpiry=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=" + appSettings.domain + "; path=/";
  };

  const getArchiveSubMenuList = useMemo(() => {
    let isSubMenuLoaded = isRoleListLoaded,
      rawList = listAudit;
    return isSubMenuLoaded
      ? rawList.map((item) => {
          let pathName = item.name;
          if (item.name === "auditTrailReport") {
            pathName = "/archive";
          } else if (item.name === "emsRepository") {
            pathName = "/ems-repo";
          }

          return pathName;
        })
      : [];
  }, [isRoleListLoaded, listAudit]);

  const getTrailMasterSubMenuList = useMemo(() => {
    let isSubMenuLoaded =
      isDropZoneFileCountsLoaded && isDropZoneSettingsLoaded;
    let subMenuList = [
      "/",
      "/zone-section",
      "/records",
      "/flagged",
      "/awaiting",
      "/finalized",
    ];
    if (
      (isSubMenuLoaded &&
        (isSharedDropZoneEnabled || isPrivateDropZoneEnabled)) ||
      (isSubMenuLoaded &&
        (!isSharedDropZoneEnabled || !isPrivateDropZoneEnabled) &&
        (dropZonePrivateFileCount > 0 || dropZoneSharedFileCount > 0))
    ) {
      subMenuList.push("/drop-zone");
    }

    return subMenuList;
  }, [
    isDropZoneSettingsLoaded,
    isDropZoneFileCountsLoaded,
    isSharedDropZoneEnabled,
    isPrivateDropZoneEnabled,
    dropZoneSharedFileCount,
    dropZonePrivateFileCount,
  ]);

  const routes = [
    {
      id: 1,
      private: false,
      path: "/oq",
      element: <h3>Hey There!!! The App is Healthy</h3>,
    },
    {
      id: 2,
      private: true,
      privateProps: {
        isLoaded: isUserRoleListLoaded,
        roleList: userRoleList,
        roleGroup: t("ADMIN.TEXT_TMF"),
        isSubMenuLoaded: isDropZoneFileCountsLoaded,
        subMenuList: getTrailMasterSubMenuList,
      },
      hasSubMenuProtection: true,
      subMenu: [
        {
          path: "/",
          element: <OverviewDashboard />,
        },
        {
          path: "/zone-section",
          element: <ZonesSections />,
        },
        {
          path: "/records",
          element: <DocumentsSection />,
        },
        {
          path: "/drop-zone",
          element: <TrailMasterDropZone />,
        },
        {
          path: "/flagged",
          element: <FlaggedByQcDocument />,
        },
        {
          path: "/awaiting",
          element: <AwatingReviewDocument />,
        },
        {
          path: "/finalized",
          element: <FinalizedDocument />,
        },
      ],
    },
    {
      id: 3,
      private: true,
      privateProps: {
        isLoaded: isUserRoleListLoaded,
        roleList: userRoleList,
        roleGroup: t("ADMIN.TEXT_TMFADMIN"),
      },
      hasSubMenuProtection: false,
      subMenu: [
        {
          path: "/tmf-structure",
          element: <TmfStructure />,
        },
        {
          path: "/tmf-setting",
          element: <OtherSettings />,
        },
        {
          path: "/templates",
          element: <Templates />,
        },
        {
          path: "/status",
          element: <Status />,
        },
      ],
    },
    {
      id: 4,
      private: true,
      privateProps: {
        isLoaded: isUserRoleListLoaded,
        roleList: userRoleList,
        roleGroup: t("ADMIN.TEXT_ARCHIVE"),
        isSubMenuLoaded: isRoleListLoaded,
        subMenuList: getArchiveSubMenuList,
      },
      hasSubMenuProtection: true,
      subMenu: [
        {
          path: "/archive",
          element: <ArchiveDashboard />,
        },
        {
          path: "/ems-repo",
          element: <Ems />,
        },
      ],
    },
    {
      id: 5,
      private: true,
      privateProps: {
        isLoaded: isUserRoleListLoaded,
        roleList: userRoleList,
        roleGroup: "",
      },
      hasSubMenuProtection: false,
      subMenu: [
        {
          path: "*",
          element: <OverviewDashboard />,
        },
      ],
    },
  ];

  const generateRoutes = (routes) => {
    return routes.map((route) => {
      if (!route.private) {
        return (
          <Route key={route.id} path={route.path} element={route.element} />
        );
      } else {
        return (
          <Route
            key={route.id}
            element={
              <PrivateRoutes
                isLoaded={route.privateProps.isLoaded}
                roleList={route.privateProps.roleList}
                roleGroup={route.privateProps.roleGroup}
                hasSubMenuProtection={route.hasSubMenuProtection}
                isSubMenuLoaded={route.privateProps.isSubMenuLoaded}
                subMenuList={route.privateProps.subMenuList}
                sidepanelToggle={showSidebar}
              />
            }
          >
            {route.subMenu.map((sub) => (
              <Route key={sub.path} path={sub.path} element={sub.element} />
            ))}
          </Route>
        );
      }
    });
  };

  return (
    <>
      <main className="container-fluid">
        {location.pathname !== "/oq" && (
          <Header setShowSidebar={setShowSidebar} />
        )}
        <div className="wrapper">
          <SidePanel
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            sidePanelRef={sidePanelRef}
          />
          <MainContainer showSidebar={showSidebar}>
            <Routes>{generateRoutes(routes)}</Routes>
          </MainContainer>
        </div>
      </main>
    </>
  );
}

export default App;
